import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LayersControl, Map as LeafletMap, Marker, Popup, TileLayer, WMSTileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import L from 'leaflet';

import { ISubmissionPayloadItem } from '@containers/common/CitizenMapView/types';

import { defaultZoom, getAgencyBoundary, getBaseLayers, getCenter, getKMLLayers } from '@utils/MapUtil';
import { getIsMobile } from '@utils/WindowUtils';

import 'leaflet.markercluster';

import './style.scss';

export interface IPublicMapViewHandle {
  focusIssue: (id: number) => void;
}

export interface IPublicMapViewProps {
  mapConfig: IMapConfig | null;
  issues: ISubmissionPayloadItem[];
  onViewportChange: (bbox: IPublicMapBbox) => void;
  onMarkerClick?: (id: number) => void;
  selectedId?: number | null;
}

interface IMarkerInstance {
  leafletElement: L.Marker;
}

interface IClusterGroupInstance {
  leafletElement: L.MarkerClusterGroup;
}

const openMarkerIcon = L.divIcon({
  html: '<span></span><span class="sr-only">Open Issue</span>',
  className: 'public-map-marker public-map-marker--open',
  iconSize: L.point(14, 14),
  iconAnchor: L.point(7, 7),
});

const closedMarkerIcon = L.divIcon({
  html: '<span></span><span class="sr-only">Closed Issue</span>',
  className: 'public-map-marker public-map-marker--closed',
  iconSize: L.point(14, 14),
  iconAnchor: L.point(7, 7),
});

const selectedMobileMarkerIcon = L.divIcon({
  html: '<span></span><span class="sr-only">Selected Issue</span>',
  className: 'public-map-marker public-map-marker--selected-mobile',
  iconSize: L.point(22, 22),
  iconAnchor: L.point(11, 11),
});

const createClusterIcon = (cluster: L.MarkerCluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 30 : count < 50 ? 40 : 50;

  return L.divIcon({
    html: `<span aria-hidden="true">${count}</span><span class="sr-only">${count} markers in cluster</span>`,
    className: 'public-map-cluster',
    iconSize: L.point(size, size),
  });
};

const PublicMapView = React.forwardRef<IPublicMapViewHandle, IPublicMapViewProps>(
  ({ mapConfig, issues, onViewportChange, onMarkerClick, selectedId }, ref) => {
    const mapRef = useRef<LeafletMap>(null);
    const clusterGroupRef = useRef<IClusterGroupInstance | null>(null);
    const markerRefs = useRef<Map<number, IMarkerInstance>>(new Map());
    const [isMobile] = useState(getIsMobile);

    const center = getCenter(mapConfig);
    const zoom = mapConfig ? mapConfig.zoomLevel : defaultZoom;
    const baseLayers = getBaseLayers(mapConfig);
    const kmlLayers = getKMLLayers(mapConfig);
    const agencyBoundaryLayer = getAgencyBoundary(mapConfig);

    const overlayLayers: IMapLayer[] = [
      ...(agencyBoundaryLayer ? [{ ...agencyBoundaryLayer, checked: true }] : []),
      ...kmlLayers,
    ];

    useEffect(() => {
      handleViewportChange();
    }, []);

    useImperativeHandle(ref, () => ({
      focusIssue(id: number) {
        const marker = markerRefs.current.get(id);
        if (!marker) {
          return;
        }
        clusterGroupRef.current?.leafletElement.zoomToShowLayer(marker.leafletElement, () => {
          if (!getIsMobile()) {
            marker.leafletElement.openPopup();
          }
        });
      },
    }));

    const handleViewportChange = () => {
      if (!mapRef.current) {
        return;
      }
      const bounds = mapRef.current.leafletElement.getBounds();
      onViewportChange({
        bboxMinLat: bounds.getSouth(),
        bboxMaxLat: bounds.getNorth(),
        bboxMinLng: bounds.getWest(),
        bboxMaxLng: bounds.getEast(),
      });
    };

    return (
      <div className="map-container" style={{ height: '100%', width: '100%' }}>
        <LeafletMap
          ref={mapRef}
          center={[center.lat, center.long] as L.LatLngExpression}
          zoom={zoom}
          maxZoom={18}
          scrollWheelZoom={true}
          attributionControl={false}
          style={{ height: '100%' }}
          onmoveend={handleViewportChange}
          onzoomend={handleViewportChange}
        >
          <LayersControl position="bottomleft">
            {baseLayers.map((layer, index) => (
              <LayersControl.BaseLayer checked={layer.checked} name={layer.label || layer.name} key={index}>
                <TileLayer url={layer.url} />
              </LayersControl.BaseLayer>
            ))}
          </LayersControl>

          {!isMobile &&
            overlayLayers.map((layer, index) => (
              <WMSTileLayer
                key={index}
                layers={layer.name}
                url={layer.url}
                format="image/png"
                maxZoom={19}
                transparent={true}
              />
            ))}

          <MarkerClusterGroup
            ref={clusterGroupRef}
            iconCreateFunction={createClusterIcon}
            disableClusteringAtZoom={15}
            spiderfyOnMaxZoom={false}
            className="public-map-cluster-group"
          >
            {issues.map((issue) => {
              const lat = issue.location?.userPinLat ?? issue.location?.lat;
              const long = issue.location?.userPinLong ?? issue.location?.long;

              if (lat == null || long == null) {
                return null;
              }

              const isSelectedOnMobile = isMobile && issue.id === selectedId;
              const icon = isSelectedOnMobile
                ? selectedMobileMarkerIcon
                : issue.status === 'OPEN'
                ? openMarkerIcon
                : closedMarkerIcon;

              return (
                <Marker
                  key={issue.id}
                  position={[lat, long]}
                  icon={icon}
                  title={`${issue.status === 'OPEN' ? 'Open' : 'Closed'} Issue: ${issue.category.name}`}
                  alt={`${issue.status === 'OPEN' ? 'Open' : 'Closed'} Issue: ${issue.category.name}`}
                  ref={(el) => {
                    if (el) {
                      markerRefs.current.set(issue.id, el as unknown as IMarkerInstance);
                    } else {
                      markerRefs.current.delete(issue.id);
                    }
                  }}
                  onClick={() => onMarkerClick && onMarkerClick(issue.id)}
                >
                  {!isSelectedOnMobile && (
                    <Popup>
                      <div>
                        <strong>{issue.category.name}</strong>
                        {issue.location?.streetAddress && (
                          <>
                            <br />
                            {issue.location.streetAddress}
                          </>
                        )}
                      </div>
                    </Popup>
                  )}
                </Marker>
              );
            })}
          </MarkerClusterGroup>
        </LeafletMap>
      </div>
    );
  }
);

PublicMapView.displayName = 'PublicMapView';

export default PublicMapView;
